/**
 * Tradução de uma falha de requisição para uma frase que o usuário entenda.
 *
 * Contexto: o interceptor de resposta de `@/axios-auth` resolve qualquer erro HTTP
 * como `Promise.resolve({ error })` (axios-auth.js:36-40). Ou seja, um 500 NÃO cai
 * no `.catch()` — ele chega no `.then()` como `{ error }`, sem `data`. Quem não
 * checa `res.error` acaba lendo `res.data.message` de um `undefined`, e é esse
 * TypeError — não o erro HTTP — que aciona o `.catch()`.
 *
 * O resultado prático era a mesma frase para tudo: "Não foi possível contactar, ou
 * você não possui permissão!" para 500, 403, timeout e queda de rede. Foi ela que
 * escondeu por semanas um 500 real (lista de e-mails estourando o `varchar(255)` de
 * `cadastros.email`), acusando falsamente falta de permissão.
 *
 * Uso no `.then()`:
 *
 *     if (res.error) {
 *       Notify.create({
 *         message: mensagemDeErro(res.error, "salvar a receita"),
 *         color: "red",
 *         position: "top-right"
 *       });
 *       return;
 *     }
 */

/**
 * Primeira mensagem de validação devolvida pelo backend.
 *
 * O shape varia conforme quem montou o erro: `{ email: { maxLength: '...' } }` quando
 * vem do validator do CakePHP, e `{ cpfcnpj: '...' }` quando o controller monta na mão.
 *
 * @param {Object|null|undefined} errors Conteúdo de `data.errors` da resposta.
 * @returns {string|null} A primeira mensagem encontrada, ou null.
 */
export function mensagemDeValidacao(errors) {
  if (!errors) return null;

  const campos = Object.keys(errors);

  for (let i = 0; i < campos.length; i++) {
    const erro = errors[campos[i]];

    if (typeof erro === "string") return erro;

    if (erro && typeof erro === "object") {
      const regras = Object.keys(erro);

      for (let j = 0; j < regras.length; j++) {
        if (typeof erro[regras[j]] === "string") return erro[regras[j]];
      }
    }
  }

  return null;
}

/**
 * Mensagem de tela para o `error` que o interceptor entregou em `res.error`.
 *
 * REGRA DE SEGURANÇA: só vai à tela a mensagem que o backend escreveu PARA O USUÁRIO
 * — a de um 403, ou a de uma resposta dentro do contrato da API (que sempre traz
 * `success`). Exceção não tratada não passa daqui: o ExceptionRenderer do CakePHP
 * responde `{ message, url, code }` SEM `success` e, com `debug` ligado, esse
 * `message` é o texto cru da exceção (`SQLSTATE[22001]`, fragmento de SQL, caminho de
 * arquivo). Para esses, só o status vai à tela.
 *
 * @param {Object} error Objeto de erro do axios (`res.error`).
 * @param {string} acao O que falhou, em infinitivo — ex.: "salvar a receita".
 *                      Entra na frase genérica: "Não foi possível {acao} (erro 500)."
 * @returns {string} Mensagem pronta para o Notify/Dialog.
 */
export function mensagemDeErro(error, acao) {
  const response = error && error.response;
  const oQueFalhou = acao || "concluir a ação";

  // Sem `response`: timeout, DNS, CORS, offline. Nunca foi questão de permissão.
  if (!response) {
    return "Não foi possível contactar o servidor. Verifique sua conexão e tente novamente.";
  }

  const data = response.data;
  const generica = `Não foi possível ${oQueFalhou} (erro ${response.status}). Tente novamente.`;

  // 403: quem explica a falta de permissão é o backend — mesmo formato que
  // comissoes.vue:936 e recorrencias.vue:828 já usam.
  if (response.status === 403) {
    return (data && data.message) || "Você não possui permissão para esta ação.";
  }

  // Resposta dentro do contrato da API: `success` presente ⇒ texto escrito para o
  // usuário. O erro de campo tem prioridade sobre o `message`, que costuma ser
  // genérico ("Erro ao cadastrar!") e não diz o que corrigir.
  if (data && data.success === false) {
    return mensagemDeValidacao(data.errors) || data.message || generica;
  }

  return generica;
}

/**
 * Erros de campo de uma resposta de erro, para ligar no `:error` dos inputs.
 *
 * Devolve `null` quando a resposta não está no contrato da API — assim a tela não
 * tenta pintar campo a partir de um payload de exceção.
 *
 * @param {Object} error Objeto de erro do axios (`res.error`).
 * @returns {Object|null} Conteúdo de `data.errors`, ou null.
 */
export function errosDeCampo(error) {
  const data = error && error.response && error.response.data;

  if (!data || data.success !== false) return null;

  return data.errors || null;
}
